import React, { type FC, useEffect } from 'react';
import { DataTable, type DataTableStateEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { noop } from '../../utils/GeneralUtils';
import DateUtils from '../../utils/DateTimeUtils';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useTenantStore, { selectTenantId } from '../../stores/TenantStore';
import OpenAPI from '../../endpoint/Openapi';
import { type components, type paths } from '../../endpoint/Schema';

dayjs.extend(duration);
const ROWS = 10;

type TableParams = Pick<DataTableStateEvent, 'first' | 'rows'>;

const Alerts: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    setLazyParams({
      first: +(searchParams.get('first') ?? '0'),
      rows: ROWS,
    });
  }, [searchParams]);

  const tenantId = useTenantStore(selectTenantId);

  const [lazyParams, setLazyParams] = React.useState<TableParams>({
    first: 0,
    rows: ROWS,
  });
  const [loading, setLoading] = React.useState(false);
  const [data, setData] =
    React.useState<
      paths['/api/v2/admin/crash']['get']['responses']['200']['content']['application/json']
    >();

  React.useEffect(() => {
    const getTrips = async () => {
      setLoading(true);

      try {
        const res = await OpenAPI.GET('/api/v2/admin/crash', {
          params: {
            query: {
              tenantId,
              skip: lazyParams.first,
              take: lazyParams.rows,
            },
          },
        });

        setData(res.data);
      } catch {}

      setLoading(false);
    };

    getTrips().catch(noop);
  }, [lazyParams.first, lazyParams.rows, tenantId]);

  function handlePageChange(event: DataTableStateEvent) {
    const params = event as unknown as TableParams;
    const o = {
      first: params.first.toString(),
      rows: params.rows.toString(),
    };

    setSearchParams(o);
  }

  function idBodyTemplate(
    rowData: components['schemas']['TenantCrashDataDto'],
  ) {
    return (
      <button
        className="text-primary underline"
        onClick={() => {
          navigate('/user/' + rowData.appUserId);
        }}
      >
        {rowData.appUserId}
      </button>
    );
  }

  function nameBodyTemplate(
    rowData: components['schemas']['TenantCrashDataDto'],
  ) {
    return [rowData.givenName, rowData.sureName].filter(Boolean).join(' ');
  }

  function dateTimeBodyTemplate(
    rowData: components['schemas']['TenantCrashDataDto'],
  ) {
    return DateUtils.formatDateTime(rowData.timeStamp);
  }

  return (
    <div className="w-full">
      <DataTable
        value={data?.page ?? []}
        scrollable
        paginator
        rows={ROWS}
        first={lazyParams.first}
        totalRecords={data?.count ?? 0}
        loading={loading}
        rowHover
        lazy
        onPage={handlePageChange}
      >
        <Column field="appUserId" header="User id" body={idBodyTemplate} />
        <Column field="givenName" header="Name" body={nameBodyTemplate} />
        <Column field="email" header="EMail" />
        <Column
          field="timeStamp"
          header="Date time"
          body={dateTimeBodyTemplate}
        />
      </DataTable>
    </div>
  );
};

export default Alerts;
